import { useState, useEffect, useRef } from "react";

/* ── hooks ───────────────────────────────────────────────── */
const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
};

const useWindowWidth = () => {
  const [w, setW] = useState(typeof window !== "undefined" ? window.innerWidth : 1200);
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener("resize", h);
    return () => window.removeEventListener("resize", h);
  }, []);
  return w;
};

/* ── tokens ──────────────────────────────────────────────── */
const GOLD  = "#F0A500";
const GOLD2 = "#E8820C";
const BG    = "#0D1117";
const SURF  = "#161B22";
const BDR   = "#21262D";
const TEXT  = "#E6EDF3";
const MUTED = "#8B949E";

/* ── global CSS ──────────────────────────────────────────── */
const css = `
@import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:ital,opsz,wght@0,9..40,300;0,9..40,400;0,9..40,500;1,9..40,300&display=swap');
*{box-sizing:border-box;margin:0;padding:0}
html{scroll-behavior:smooth}
body{background:${BG};color:${TEXT};font-family:'DM Sans',sans-serif;overflow-x:hidden}
::-webkit-scrollbar{width:3px}::-webkit-scrollbar-track{background:${BG}}::-webkit-scrollbar-thumb{background:${GOLD};border-radius:3px}

@keyframes float{0%,100%{transform:translateY(0)}50%{transform:translateY(-14px)}}
@keyframes spin{to{transform:rotate(360deg)}}
@keyframes spinR{to{transform:rotate(-360deg)}}
@keyframes pulse{0%,100%{opacity:.5;transform:scale(1)}50%{opacity:1;transform:scale(1.1)}}
@keyframes shimmer{0%{background-position:-300% center}100%{background-position:300% center}}
@keyframes blink{0%,100%{opacity:1}50%{opacity:0}}
@keyframes glow{0%,100%{box-shadow:0 0 0 1px ${GOLD}33}50%{box-shadow:0 0 24px 2px ${GOLD}44,0 0 48px 2px ${GOLD}18}}
@keyframes slideDown{from{opacity:0;transform:translateY(-12px)}to{opacity:1;transform:translateY(0)}}

.ch{transition:transform .3s ease,box-shadow .3s ease,border-color .3s ease}
.ch:hover{transform:translateY(-4px)!important;box-shadow:0 14px 48px rgba(240,165,0,.13)!important}
.lh{transition:color .2s}.lh:hover{color:${GOLD}!important}

.hb span{display:block;width:22px;height:2px;background:${TEXT};border-radius:2px;transition:all .3s ease}
.hb.open span:nth-child(1){transform:rotate(45deg) translate(5px,5px)}
.hb.open span:nth-child(2){opacity:0;transform:scaleX(0)}
.hb.open span:nth-child(3){transform:rotate(-45deg) translate(5px,-5px)}

@media(max-width:768px){
  .hero-row{flex-direction:column!important;text-align:center;align-items:center!important}
  .hero-cta{justify-content:center!important}
  .exp-row{grid-template-columns:1fr!important}
  .exp-stick{position:static!important}
  .stats-row{grid-template-columns:repeat(3,1fr)!important}
  .footer-row{flex-direction:column!important;text-align:center;gap:8px!important}
}
@media(max-width:480px){
  .stats-row{grid-template-columns:repeat(2,1fr)!important}
}
`;

/* ── helpers ─────────────────────────────────────────────── */
const FadeUp = ({ children, delay = 0 }) => {
  const [ref, vis] = useInView();
  return (
    <div ref={ref} style={{ opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(36px)", transition: `opacity .7s ease ${delay}s,transform .7s ease ${delay}s` }}>
      {children}
    </div>
  );
};

const Counter = ({ target, suffix = "", vis }) => {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!vis) return;
    const num = parseFloat(target);
    if (isNaN(num)) return;
    let cur = 0; const step = num / 50;
    const t = setInterval(() => { cur += step; if (cur >= num) { setN(num); clearInterval(t); } else setN(Math.round(cur)); }, 24);
    return () => clearInterval(t);
  }, [vis, target]);
  if (isNaN(parseFloat(target))) return <>{target}</>;
  return <>{n.toLocaleString()}{suffix}</>;
};

const SectionHead = ({ title }) => (
  <div style={{ marginBottom: "44px" }}>
    <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "10px" }}>
      <div style={{ width: "36px", height: "2px", background: `linear-gradient(90deg,${GOLD},${GOLD2})` }} />
      <span style={{ fontSize: "11px", color: GOLD, letterSpacing: "3px", textTransform: "uppercase", fontWeight: 500 }}>Portfolio</span>
    </div>
    <h2 style={{ fontFamily: "'Syne',sans-serif", fontSize: "clamp(24px,4vw,40px)", fontWeight: 800, color: TEXT }}>{title}</h2>
  </div>
);

/* ── navbar ──────────────────────────────────────────────── */
const NavBar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const w = useWindowWidth();
  const isMobile = w < 768;
  useEffect(() => { const h = () => setScrolled(window.scrollY > 50); window.addEventListener("scroll", h); return () => window.removeEventListener("scroll", h); }, []);
  useEffect(() => { if (!isMobile) setOpen(false); }, [isMobile]);
  const links = ["About", "Skills", "Experience", "Projects", "Contact"];
  const px = isMobile ? "14px 20px" : "16px 48px";
  return (
    <>
      <nav style={{ position: "fixed", top: 0, left: 0, right: 0, zIndex: 200, padding: px, display: "flex", justifyContent: "space-between", alignItems: "center", background: scrolled || open ? `${SURF}f2` : "transparent", backdropFilter: scrolled || open ? "blur(20px)" : "none", borderBottom: scrolled || open ? `1px solid ${BDR}` : "none", transition: "all .4s ease" }}>
        <div style={{ fontFamily: "'Syne',sans-serif", fontSize: "22px", fontWeight: 800, background: `linear-gradient(90deg,${GOLD},${GOLD2})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>VY</div>
        {!isMobile && (
          <div style={{ display: "flex", gap: "32px" }}>
            {links.map(l => <a key={l} href={`#${l.toLowerCase()}`} className="lh" style={{ fontSize: "12px", fontWeight: 500, color: MUTED, textDecoration: "none", letterSpacing: "1.2px", textTransform: "uppercase" }}>{l}</a>)}
          </div>
        )}
        {isMobile && (
          <button className={`hb ${open ? "open" : ""}`} onClick={() => setOpen(o => !o)} style={{ background: "none", border: "none", cursor: "pointer", display: "flex", flexDirection: "column", gap: "5px", padding: "4px" }}>
            <span /><span /><span />
          </button>
        )}
      </nav>
      {isMobile && open && (
        <div style={{ position: "fixed", top: "53px", left: 0, right: 0, zIndex: 190, background: `${SURF}f8`, backdropFilter: "blur(20px)", borderBottom: `1px solid ${BDR}`, padding: "16px 24px", animation: "slideDown .25s ease", display: "flex", flexDirection: "column" }}>
          {links.map(l => <a key={l} href={`#${l.toLowerCase()}`} onClick={() => setOpen(false)} style={{ fontSize: "15px", fontWeight: 500, color: TEXT, textDecoration: "none", padding: "13px 0", borderBottom: `1px solid ${BDR}` }}>{l}</a>)}
        </div>
      )}
    </>
  );
};

/* ── hero ────────────────────────────────────────────────── */
const Hero = () => {
  const [typed, setTyped] = useState("");
  const [roleIdx, setRoleIdx] = useState(0);
  const [del, setDel] = useState(false);
  const roles = ["Flutter Developer", "Full-Stack Engineer", "Mobile App Developer"];
  const w = useWindowWidth();
  const isMobile = w < 768;

  useEffect(() => {
    const cur = roles[roleIdx];
    const t = setTimeout(() => {
      if (!del) { if (typed.length < cur.length) setTyped(cur.slice(0, typed.length + 1)); else setTimeout(() => setDel(true), 2000); }
      else { if (typed.length > 0) setTyped(typed.slice(0, -1)); else { setDel(false); setRoleIdx((roleIdx + 1) % roles.length); } }
    }, del ? 45 : 95);
    return () => clearTimeout(t);
  }, [typed, del, roleIdx]);

  return (
    <section id="about" style={{ minHeight: "100vh", display: "flex", alignItems: "center", position: "relative", overflow: "hidden", padding: isMobile ? "90px 20px 60px" : "100px 60px 60px" }}>
      <div style={{ position: "absolute", top: "15%", right: isMobile ? "-8%" : "8%", width: isMobile ? "240px" : "420px", height: isMobile ? "240px" : "420px", borderRadius: "50%", background: `radial-gradient(circle,${GOLD}16 0%,transparent 70%)`, animation: "float 7s ease-in-out infinite", pointerEvents: "none" }} />
      <div style={{ position: "absolute", bottom: "8%", left: isMobile ? "-8%" : "4%", width: isMobile ? "180px" : "300px", height: isMobile ? "180px" : "300px", borderRadius: "50%", background: `radial-gradient(circle,${GOLD2}10 0%,transparent 70%)`, animation: "float 9s ease-in-out infinite reverse", pointerEvents: "none" }} />
      <div style={{ position: "absolute", inset: 0, backgroundImage: `linear-gradient(${BDR}44 1px,transparent 1px),linear-gradient(90deg,${BDR}44 1px,transparent 1px)`, backgroundSize: "60px 60px", opacity: .32, pointerEvents: "none" }} />

      <div className="hero-row" style={{ maxWidth: "1140px", margin: "0 auto", width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center", gap: isMobile ? "36px" : "60px", position: "relative", zIndex: 1 }}>
        <div style={{ flex: 1 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: "8px", background: `${GOLD}18`, border: `1px solid ${GOLD}44`, borderRadius: "100px", padding: "6px 16px", marginBottom: "22px" }}>
            <div style={{ width: "7px", height: "7px", borderRadius: "50%", background: GOLD, animation: "pulse 2s ease-in-out infinite" }} />
            <span style={{ fontSize: "11px", color: GOLD, letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 500 }}>Available for Work</span>
          </div>
          <h1 style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? "clamp(28px,8vw,40px)" : "clamp(38px,5vw,62px)", fontWeight: 800, lineHeight: 1.1, marginBottom: "16px" }}>
            Yenuganti Veera<br />
            <span style={{ background: `linear-gradient(90deg,${GOLD} 0%,${GOLD2} 50%,${GOLD} 100%)`, backgroundSize: "300% auto", WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent", animation: "shimmer 3s linear infinite" }}>Vasantharao</span>
          </h1>
          <div style={{ fontSize: isMobile ? "17px" : "21px", fontWeight: 300, color: MUTED, marginBottom: "22px", minHeight: "30px" }}>
            <span style={{ color: TEXT }}>{typed}</span>
            <span style={{ color: GOLD, animation: "blink 1s step-end infinite" }}>|</span>
          </div>
          <p style={{ fontSize: isMobile ? "14px" : "15px", color: MUTED, lineHeight: 1.9, maxWidth: "520px", marginBottom: "32px", margin: isMobile ? "0 auto 32px" : "0 0 32px" }}>
            3+ years crafting high-performance cross-platform apps.&nbsp;
            <span style={{ color: GOLD, fontWeight: 500 }}>25,000+ active users</span> across Android & iOS.
          </p>
          <div className="hero-cta" style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
            <a href="#contact" style={{ padding: isMobile ? "11px 22px" : "13px 30px", background: `linear-gradient(135deg,${GOLD},${GOLD2})`, color: "#0D1117", fontWeight: 700, fontSize: "13px", borderRadius: "6px", textDecoration: "none", fontFamily: "'Syne',sans-serif" }}>Get In Touch</a>
            <a href="#experience" style={{ padding: isMobile ? "11px 22px" : "13px 30px", border: `1px solid ${BDR}`, color: TEXT, fontWeight: 500, fontSize: "13px", borderRadius: "6px", textDecoration: "none", transition: "border-color .2s" }}
               onMouseEnter={e => e.target.style.borderColor = GOLD} onMouseLeave={e => e.target.style.borderColor = BDR}>View Work</a>
          </div>
        </div>

        <div style={{ flexShrink: 0, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ position: "absolute", inset: isMobile ? "-13px" : "-18px", borderRadius: "50%", border: `1px dashed ${GOLD}55`, animation: "spin 22s linear infinite" }} />
          <div style={{ position: "absolute", inset: isMobile ? "-26px" : "-36px", borderRadius: "50%", border: `1px dashed ${GOLD}28`, animation: "spinR 32s linear infinite" }} />
          <div style={{ width: isMobile ? "150px" : "210px", height: isMobile ? "150px" : "210px", borderRadius: "50%", background: `linear-gradient(135deg,${GOLD}22 0%,${GOLD2}14 100%)`, border: `2px solid ${GOLD}`, display: "flex", alignItems: "center", justifyContent: "center", animation: "glow 3s ease-in-out infinite" }}>
            <span style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? "52px" : "70px", fontWeight: 800, background: `linear-gradient(135deg,${GOLD},${GOLD2})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>VY</span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── stats ───────────────────────────────────────────────── */
const Stats = () => {
  const [ref, vis] = useInView();
  const w = useWindowWidth();
  const isMobile = w < 768;
  const data = [
    { n: "3", s: "+", l: "Years Exp" },
    { n: "25", s: "K+", l: "Active Users" },
    { n: "2", s: "", l: "Apps Shipped" },
    { n: "30", s: "K+", l: "Platform Users" },
    { n: "3", s: "", l: "Certifications" },
  ];
  return (
    <div style={{ padding: isMobile ? "0 16px 44px" : "0 60px 56px" }}>
      <div ref={ref} className="stats-row" style={{ maxWidth: "1140px", margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: "10px" }}>
        {data.map((d, i) => (
          <div key={i} className="ch" style={{ background: SURF, border: `1px solid ${BDR}`, borderRadius: "8px", padding: isMobile ? "18px 8px" : "24px 14px", textAlign: "center", position: "relative", overflow: "hidden", opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(28px)", transition: `opacity .6s ease ${i * .09}s,transform .6s ease ${i * .09}s` }}>
            <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg,${GOLD},${GOLD2})` }} />
            <div style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? "20px" : "28px", fontWeight: 800, color: GOLD, marginBottom: "5px" }}>
              <Counter target={d.n} suffix={d.s} vis={vis} />
            </div>
            <div style={{ fontSize: isMobile ? "8px" : "10px", color: MUTED, textTransform: "uppercase", letterSpacing: "1px" }}>{d.l}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ── skills ──────────────────────────────────────────────── */
const Skills = () => {
  const [ref, vis] = useInView();
  const w = useWindowWidth();
  const isMobile = w < 768;
  const groups = [
    { name: "Mobile Development", items: ["Flutter", "Dart", "Android Studio", "Xcode"], pct: 95 },
    { name: "State Management", items: ["Provider", "GetX", "BLoC"], pct: 90 },
    { name: "Backend", items: ["Node.js", "Express.js", "REST APIs", "JWT"], pct: 85 },
    { name: "Database", items: ["MongoDB", "Firebase", "SQL"], pct: 88 },
    { name: "Languages", items: ["Dart", "JavaScript", "Java", "Python"], pct: 82 },
    { name: "Tools & DevOps", items: ["Git", "GitHub", "Postman", "Play Console"], pct: 80 },
  ];
  return (
    <section id="skills" style={{ padding: isMobile ? "60px 16px" : "100px 60px", background: SURF }}>
      <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
        <SectionHead title="Technical Skills" />
        <div ref={ref} style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(320px,1fr))", gap: "18px" }}>
          {groups.map((g, i) => (
            <div key={i} className="ch" style={{ background: BG, border: `1px solid ${BDR}`, borderRadius: "8px", padding: "22px", opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(28px)", transition: `opacity .6s ease ${i * .1}s,transform .6s ease ${i * .1}s` }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "10px" }}>
                <h3 style={{ fontFamily: "'Syne',sans-serif", fontSize: "14px", fontWeight: 700, color: TEXT }}>{g.name}</h3>
                <span style={{ fontSize: "12px", color: GOLD, fontWeight: 600 }}>{g.pct}%</span>
              </div>
              <div style={{ background: BDR, borderRadius: "2px", height: "3px", marginBottom: "14px", overflow: "hidden" }}>
                <div style={{ height: "100%", width: `${g.pct}%`, background: `linear-gradient(90deg,${GOLD},${GOLD2})`, borderRadius: "2px", transform: vis ? "scaleX(1)" : "scaleX(0)", transformOrigin: "left", transition: `transform 1.1s ease ${i * .12 + .3}s` }} />
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "7px" }}>
                {g.items.map((it, j) => <span key={j} style={{ fontSize: "11px", padding: "3px 10px", borderRadius: "100px", border: `1px solid ${BDR}`, color: MUTED }}>{it}</span>)}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── experience ──────────────────────────────────────────── */
const Experience = () => {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const bullets = [
    "Teach Flutter, Dart, Node.js, MongoDB, and Firebase to undergrad and postgrad students.",
    "Deliver curriculum covering cross-platform mobile dev, RESTful APIs, and NoSQL database design.",
    "Conduct workshops on Git/GitHub, REST API development, and MongoDB aggregation pipelines.",
    "Guide students through real-world Flutter projects — responsive UIs and scalable Node.js backends.",
    "Evaluate progress, conduct code reviews, and provide personalized Flutter mentoring.",
    "Mentor capstone projects with Firebase integration, MongoDB modeling, and app store deployment.",
  ];
  return (
    <section id="experience" style={{ padding: isMobile ? "60px 16px" : "100px 60px", background: BG }}>
      <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
        <SectionHead title="Professional Experience" />
        <div className="exp-row" style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: isMobile ? "28px" : "60px", alignItems: "start" }}>
          <FadeUp>
            <div className="exp-stick" style={{ position: "sticky", top: "88px" }}>
              <div style={{ background: SURF, border: `1px solid ${BDR}`, borderRadius: "8px", padding: "22px", borderLeft: `3px solid ${GOLD}` }}>
                <div style={{ fontFamily: "'Syne',sans-serif", fontSize: "15px", fontWeight: 700, color: TEXT, marginBottom: "6px" }}>Lecturer & Developer</div>
                <div style={{ fontSize: "13px", color: GOLD, fontWeight: 500, marginBottom: "5px" }}>Full Stack Mobile App Development</div>
                <div style={{ fontSize: "12px", color: MUTED, marginBottom: "16px" }}>Aditya University · Kakinada, AP</div>
                <div style={{ display: "inline-flex", alignItems: "center", gap: "7px", background: `${GOLD}18`, border: `1px solid ${GOLD}44`, borderRadius: "100px", padding: "5px 13px" }}>
                  <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: GOLD, animation: "pulse 2s ease-in-out infinite" }} />
                  <span style={{ fontSize: "11px", color: GOLD, fontWeight: 500 }}>Mar 2023 – Present</span>
                </div>
              </div>
            </div>
          </FadeUp>
          <div>
            {bullets.map((b, i) => (
              <FadeUp key={i} delay={i * .08}>
                <div style={{ display: "flex", gap: "16px", marginBottom: "18px", paddingBottom: "18px", borderBottom: i < bullets.length - 1 ? `1px solid ${BDR}` : "none" }}>
                  <div style={{ flexShrink: 0, marginTop: "8px", width: "9px", height: "9px", borderRadius: "50%", border: `2px solid ${GOLD}` }} />
                  <p style={{ fontSize: isMobile ? "13px" : "14px", color: MUTED, lineHeight: 1.85 }}>{b}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

/* ── achievements ────────────────────────────────────────── */
const Achievements = () => {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const items = [
    { icon: "🏆", title: "Dubai AI Week 2025", desc: "Top 20 globally at Dubai AI Week Hackathon, competing against international developers and AI specialists.", tag: "Global Recognition" },
    { icon: "📱", title: "2 Production Apps", desc: "Built and deployed 2 mobile apps supporting 25,000+ active users on Android & iOS platforms.", tag: "25K+ Users" },
    { icon: "🚀", title: "Scaled to 30K Users", desc: "Scaled a training management platform to 30,000+ active users with reliable real-time data sync.", tag: "High Scale" },
    { icon: "🔔", title: "Firebase FCM", desc: "Delivered push notification systems and optimized UI/UX components, significantly improving retention.", tag: "Engagement" },
  ];
  return (
    <section id="projects" style={{ padding: isMobile ? "60px 16px" : "100px 60px", background: SURF }}>
      <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
        <SectionHead title="Key Achievements" />
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(255px,1fr))", gap: "18px" }}>
          {items.map((it, i) => (
            <FadeUp key={i} delay={i * .09}>
              <div className="ch" style={{ background: BG, border: `1px solid ${BDR}`, borderRadius: "8px", padding: isMobile ? "22px" : "28px", position: "relative", overflow: "hidden", height: "100%" }}>
                <div style={{ position: "absolute", top: 0, left: 0, right: 0, height: "2px", background: `linear-gradient(90deg,${GOLD},${GOLD2})` }} />
                <div style={{ fontSize: "30px", marginBottom: "12px" }}>{it.icon}</div>
                <div style={{ display: "inline-block", fontSize: "10px", color: GOLD, background: `${GOLD}18`, border: `1px solid ${GOLD}44`, padding: "3px 10px", borderRadius: "100px", marginBottom: "10px" }}>{it.tag}</div>
                <h3 style={{ fontFamily: "'Syne',sans-serif", fontSize: "14px", fontWeight: 700, color: TEXT, marginBottom: "8px" }}>{it.title}</h3>
                <p style={{ fontSize: "13px", color: MUTED, lineHeight: 1.85 }}>{it.desc}</p>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── competencies ────────────────────────────────────────── */
const Competencies = () => {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const items = [
    { title: "Flutter & Mobile Dev", desc: "Expert in high-performance cross-platform apps — UI, state management, API integration, and store deployment." },
    { title: "MongoDB & DB Engineering", desc: "NoSQL schema modeling, query optimization, and aggregation pipelines for production-scale applications." },
    { title: "Backend Development", desc: "Scalable Node.js/Express systems, RESTful APIs, and secure auth flows with JWT and OAuth." },
    { title: "State Management", desc: "Provider, GetX, and BLoC — clean architecture for complex, maintainable Flutter codebases." },
    { title: "Cloud & DevOps", desc: "Firebase, Git/GitHub, and end-to-end deployment via Google Play Console and Apple App Store." },
  ];
  return (
    <section style={{ padding: isMobile ? "60px 16px" : "100px 60px", background: BG }}>
      <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
        <SectionHead title="Core Competencies" />
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(300px,1fr))", gap: "16px" }}>
          {items.map((it, i) => (
            <FadeUp key={i} delay={i * .08}>
              <div className="ch" style={{ background: SURF, border: `1px solid ${BDR}`, borderRadius: "8px", padding: "22px", display: "flex", gap: "14px", height: "100%" }}>
                <div style={{ flexShrink: 0, width: "3px", borderRadius: "2px", background: `linear-gradient(180deg,${GOLD},${GOLD2})`, alignSelf: "stretch" }} />
                <div>
                  <h3 style={{ fontFamily: "'Syne',sans-serif", fontSize: "14px", fontWeight: 700, color: TEXT, marginBottom: "8px" }}>{it.title}</h3>
                  <p style={{ fontSize: "13px", color: MUTED, lineHeight: 1.85 }}>{it.desc}</p>
                </div>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── certifications ──────────────────────────────────────── */
const Certs = () => {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const certs = ["MongoDB Associate Developer", "Google Play Store Listing Certification", "Postman API Fundamentals"];
  return (
    <section style={{ padding: isMobile ? "48px 16px" : "64px 60px", background: SURF }}>
      <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
        <SectionHead title="Certifications" />
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(260px,1fr))", gap: "14px" }}>
          {certs.map((c, i) => (
            <FadeUp key={i} delay={i * .1}>
              <div className="ch" style={{ background: BG, border: `1px solid ${BDR}`, borderRadius: "8px", padding: "16px 20px", display: "flex", alignItems: "center", gap: "14px" }}>
                <div style={{ width: "36px", height: "36px", borderRadius: "50%", background: `${GOLD}20`, border: `1px solid ${GOLD}55`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", flexShrink: 0 }}>🏅</div>
                <span style={{ fontFamily: "'Syne',sans-serif", fontSize: "13px", fontWeight: 600, color: TEXT }}>{c}</span>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
};

/* ── contact ─────────────────────────────────────────────── */
const Contact = () => {
  const w = useWindowWidth();
  const isMobile = w < 768;
  const contacts = [
    { icon: "📧", label: "Email", value: "vasanthyenuganti@adityauniversity.in", href: "mailto:vasanthyenuganti@adityauniversity.in" },
    { icon: "💼", label: "LinkedIn", value: "linkedin.com/in/vasanthyenuganti", href: "#" },
    { icon: "📍", label: "Location", value: "Kakinada, Andhra Pradesh", href: null },
    { icon: "🌐", label: "GitHub", value: "github.com/vasanthyenuganti", href: "#" },
  ];
  return (
    <section id="contact" style={{ padding: isMobile ? "60px 16px" : "100px 60px", background: BG }}>
      <div style={{ maxWidth: "1140px", margin: "0 auto" }}>
        <SectionHead title="Get In Touch" />
        <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(auto-fit,minmax(235px,1fr))", gap: "14px", marginBottom: "44px" }}>
          {contacts.map((c, i) => (
            <FadeUp key={i} delay={i * .09}>
              <a href={c.href || "#"} className="ch" style={{ display: "flex", alignItems: "center", gap: "14px", background: SURF, border: `1px solid ${BDR}`, borderRadius: "8px", padding: "18px", textDecoration: "none" }}
                 onMouseEnter={e => e.currentTarget.style.borderColor = `${GOLD}77`}
                 onMouseLeave={e => e.currentTarget.style.borderColor = BDR}>
                <div style={{ width: "40px", height: "40px", borderRadius: "8px", background: `${GOLD}18`, border: `1px solid ${GOLD}44`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>{c.icon}</div>
                <div>
                  <div style={{ fontSize: "10px", color: GOLD, letterSpacing: "1.5px", textTransform: "uppercase", fontWeight: 500, marginBottom: "3px" }}>{c.label}</div>
                  <div style={{ fontSize: isMobile ? "12px" : "13px", color: TEXT }}></div>
                </div>
              </a>
            </FadeUp>
          ))}
        </div>

        <FadeUp delay={0.3}>
          <div style={{ background: `linear-gradient(135deg,${GOLD}18 0%,${GOLD2}10 100%)`, border: `1px solid ${GOLD}33`, borderRadius: "12px", padding: isMobile ? "28px 20px" : "48px", textAlign: "center" }}>
            <h3 style={{ fontFamily: "'Syne',sans-serif", fontSize: isMobile ? "20px" : "26px", fontWeight: 800, color: TEXT, marginBottom: "10px" }}>Let's Build Something Great</h3>
            <p style={{ fontSize: "14px", color: MUTED, marginBottom: "24px", lineHeight: 1.8 }}>Open to Flutter roles, full-stack projects, and collaborative opportunities.</p>
            <a href="mailto:vasanthyenuganti@adityauniversity.in" style={{ display: "inline-block", padding: isMobile ? "12px 28px" : "14px 36px", background: `linear-gradient(135deg,${GOLD},${GOLD2})`, color: "#0D1117", fontWeight: 700, fontSize: "14px", borderRadius: "6px", textDecoration: "none", fontFamily: "'Syne',sans-serif" }}>Say Hello →</a>
          </div>
        </FadeUp>
      </div>
    </section>
  );
};

/* ── footer ──────────────────────────────────────────────── */
const Footer = () => (
  <footer style={{ background: SURF, borderTop: `1px solid ${BDR}`, padding: "24px 20px" }}>
    <div className="footer-row" style={{ maxWidth: "1140px", margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "10px" }}>
      <div style={{ fontFamily: "'Syne',sans-serif", fontSize: "14px", fontWeight: 800, background: `linear-gradient(90deg,${GOLD},${GOLD2})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>Yenuganti Veera Vasantharao</div>
      <div style={{ fontSize: "12px", color: MUTED }}>Flutter · Full-Stack · Mobile</div>
      <div style={{ fontSize: "12px", color: GOLD, fontWeight: 500 }}>© 2025 — All rights reserved</div>
    </div>
  </footer>
);

/* ── root ────────────────────────────────────────────────── */
export default function VasanthPortfolio() {
  return (
    <>
      <style>{css}</style>
      <NavBar />
      <Hero />
      <Stats />
      <Skills />
      <Experience />
      <Achievements />
      <Competencies />
      <Certs />
      <Contact />
      <Footer />
    </>
  );
}